import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Repeat, Pause, Play, ArrowRight } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import StatusPill from "@/components/marketplace/StatusPill";

export default function RecurringListPage() {
  const [role, setRole] = useState("customer");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useDocumentTitle("Recurring services");

  const load = () => {
    setLoading(true);
    api.get("/recurring/mine", { params: { role } }).then(async (r) => {
      const rows = r.data || [];
      // Enrich with service titles for a nicer display than raw service_id.
      const withTitles = await Promise.all(rows.map(async (s) => {
        try {
          const { data } = await api.get(`/services/${s.service_id}`);
          return { ...s, service_title: data?.service?.title || s.service_id };
        } catch { return { ...s, service_title: s.service_id }; }
      }));
      setItems(withTitles);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [role]);

  const toggle = async (id, action) => {
    try {
      await api.post(`/recurring/${id}/${action}`);
      toast.success(action === "pause" ? "Paused" : "Resumed");
      load();
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Recurring</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Recurring services</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">Weekly lawn, monthly cleans — set once, we top up 4 upcoming visits at a time.</p>
          </div>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-full border border-slate-200 px-4 py-2 text-sm bg-white" data-testid="recur-role">
            <option value="customer">As customer</option>
            <option value="provider">As provider</option>
          </select>
        </div>
        {loading ? (
          <div className="text-[#252A34]/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Repeat className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">No recurring services yet.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Book a recurring service (weekly cleans, monthly detailing, etc.) to see them here.</div>
            <Link to="/services?service_type=recurring_service" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium">Find recurring services <ArrowRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((s) => (
              <div key={s.id} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-4" data-testid={`recur-row-${s.id}`}>
                <div className="h-12 w-12 rounded-xl bg-[#165a71]/10 flex items-center justify-center text-[#165a71]"><Repeat className="h-5 w-5" /></div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-display font-semibold text-[#071b22]">{s.service_title || s.service_id}</div>
                    <StatusPill status={s.status} />
                  </div>
                  <div className="text-xs text-[#252A34]/60 mt-1 flex flex-wrap items-center gap-3">
                    <span>{s.frequency} · {s.days_of_week?.length ? `${s.days_of_week.length} day/wk` : ""}</span>
                    <span>· ${s.price_per_occurrence?.toFixed(2)}/visit</span>
                    <span>· {s.generated_count || 0} upcoming</span>
                  </div>
                </div>
                <div>
                  {s.status === "active" ? (
                    <button className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1" onClick={() => toggle(s.id, "pause")} data-testid={`pause-${s.id}`}><Pause className="h-3.5 w-3.5" /> Pause</button>
                  ) : (
                    <button className="rounded-full bg-[#165a71] text-white px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1" onClick={() => toggle(s.id, "resume")} data-testid={`resume-${s.id}`}><Play className="h-3.5 w-3.5" /> Resume</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
